import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search, Camera, X } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { useEscClose } from '../hooks/useEscClose';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GOLD = '#D89232';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';
const CREAM = '#F5F0EA';
const ESPRESSO = '#2B2118';

// Kamera-Scan für den Code-128-Barcode auf dem Zertifikat
const BarcodeScanner = ({ onResult, onClose }) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState(null);
  useEscClose(onClose);

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE]);
    const reader = new BrowserMultiFormatReader(hints);
    let cancelled = false;

    reader
      .decodeFromVideoDevice(undefined, videoRef.current, (result, err, controls) => {
        controlsRef.current = controls;
        if (result && !cancelled) {
          cancelled = true;
          controls.stop();
          onResult(result.getText());
        }
      })
      .catch(() => {
        setError('Kamera nicht verfügbar. Bitte Kamerazugriff erlauben — oder die Nummer unten von Hand eingeben.');
      });

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch (e) { /* noop */ }
    };
  }, [onResult]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Barcode scannen" style={{ backgroundColor: 'rgba(43,33,24,0.85)' }} data-testid="barcode-scanner-modal">
      <div className="w-full max-w-md bg-cream-50 shadow-2xl" style={{ border: '1px solid rgba(216,146,50,0.5)', padding: 8, backgroundColor: CREAM }}>
        <div className="p-5" style={{ border: '1px solid rgba(216,146,50,0.5)' }}>
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
              Barcode scannen
            </p>
            <button onClick={onClose} className="text-sand-500 hover:text-espresso" data-testid="scanner-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {error ? (
            <p className="font-display italic text-[15px] py-8 text-center" style={{ color: TERRA }} data-testid="scanner-error">{error}</p>
          ) : (
            <div className="relative overflow-hidden" style={{ backgroundColor: ESPRESSO }}>
              <video ref={videoRef} className="w-full h-[300px] object-cover" muted playsInline data-testid="scanner-video" />
              {/* Ziellinie */}
              <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 pointer-events-none" style={{ height: 2, backgroundColor: 'rgba(216,146,50,0.8)' }} />
            </div>
          )}
          <p className="text-[12px] text-sand-600 mt-4 text-center">
            Halte den Barcode unten links auf dem Zertifikat vor die Kamera.
          </p>
        </div>
      </div>
    </div>
  );
};

const VerifyCertificatePage = () => {
  const { certNumber } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(certNumber || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const check = useCallback(async (num) => {
    if (!num?.trim()) return;
    setLoading(true);
    setChecked(false);
    try {
      const res = await axios.get(`${API}/verify/${encodeURIComponent(num.trim())}`);
      setData(res.data);
    } catch (e) {
      setData({ valid: false });
    } finally {
      setLoading(false);
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    if (certNumber) {
      setInput(certNumber);
      check(certNumber);
    }
  }, [certNumber, check]);

  const handleScanResult = useCallback((text) => {
    setScannerOpen(false);
    const num = (text || '').trim();
    if (!num) return;
    setInput(num);
    navigate(`/verify/${encodeURIComponent(num)}`, { replace: true });
    check(num);
  }, [check, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = input.trim();
    if (!num) return;
    navigate(`/verify/${encodeURIComponent(num)}`, { replace: true });
    check(num);
  };

  return (
    <div className="min-h-screen px-4 py-14" style={{ backgroundColor: CREAM }} data-testid="verify-page">
      <div className="max-w-xl mx-auto">
        {/* Masthead */}
        <div className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl" style={{ color: ESPRESSO, letterSpacing: '0.04em' }}>
            DICKIES-CAMPUS
          </h1>
          <p className="font-mono text-[10px] uppercase font-semibold mt-2" style={{ letterSpacing: '0.3em', color: GOLD_INK }}>
            Akademie für Pflege · Echtheitsprüfung
          </p>
          <div className="mx-auto mt-4" style={{ width: 48, height: 1, backgroundColor: GOLD }} />
        </div>

        {/* Eingabe */}
        <form onSubmit={handleSubmit} className="mt-10">
          <label className="font-mono text-[10px] uppercase font-semibold block mb-2" style={{ letterSpacing: '0.22em', color: '#8A8175' }}>
            Zertifikatsnummer
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="DH-2026-0001"
              autoFocus
              className="flex-1 h-[48px] px-4 bg-transparent font-mono text-[15px] focus:outline-none"
              style={{ border: `1px solid rgba(216,146,50,0.5)`, color: ESPRESSO }}
              data-testid="verify-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-[48px] px-7 rounded-full bg-transparent text-[12px] font-bold uppercase inline-flex items-center gap-2 transition-colors disabled:opacity-40"
              style={{ border: `1.5px solid ${TERRA}`, color: TERRA, letterSpacing: '0.16em' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TERRA; e.currentTarget.style.color = CREAM; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TERRA; }}
              data-testid="verify-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Prüfen
            </button>
          </div>
        </form>

        {/* Kamera-Scan */}
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold hover:underline underline-offset-4"
          style={{ color: TERRA }}
          data-testid="open-scanner-btn"
        >
          <Camera className="w-4 h-4" />
          Barcode mit der Kamera scannen
        </button>

        {scannerOpen && <BarcodeScanner onResult={handleScanResult} onClose={() => setScannerOpen(false)} />}

        {/* Ergebnis */}
        {checked && !loading && data && (
          data.valid ? (
            <div className="mt-10 bg-cream-100 shadow-soft" style={{ border: '1px solid rgba(216,146,50,0.4)', padding: 8 }} data-testid="verify-valid">
              <div className="px-7 py-8" style={{ border: '1px solid rgba(216,146,50,0.4)' }}>
                {/* Status */}
                <div className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: GOLD }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </span>
                  <span className="font-mono text-[12px] uppercase font-bold" style={{ letterSpacing: '0.28em', color: GOLD_INK }} data-testid="verify-status">
                    Gültig
                  </span>
                  <span className="ml-auto font-mono text-[10px] uppercase text-sand-500" style={{ letterSpacing: '0.14em' }}>
                    Nº {data.certificate_number}
                  </span>
                </div>

                <div className="my-5" style={{ height: 1, backgroundColor: 'rgba(216,146,50,0.35)' }} />

                {/* Details */}
                <p className="font-display italic text-[15px] text-sand-600">ausgestellt für</p>
                <p className="font-display text-[28px] mt-1" style={{ color: TERRA }} data-testid="verify-name">{data.participant_name}</p>

                <p className="font-display text-[18px] mt-5" style={{ color: ESPRESSO }} data-testid="verify-course">{data.course_title}</p>

                <div className="flex flex-wrap gap-x-10 gap-y-3 mt-6">
                  <div>
                    <p className="font-mono text-[9px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: GOLD_INK }}>Umfang</p>
                    <p className="font-display text-[16px] mt-1" style={{ color: ESPRESSO }} data-testid="verify-ue">{data.total_ue} UE</p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: GOLD_INK }}>Abschlussdatum</p>
                    <p className="font-display text-[16px] mt-1" style={{ color: ESPRESSO }} data-testid="verify-date">{data.issued_at}</p>
                  </div>
                </div>

                <p className="font-display italic text-[12.5px] text-sand-500 mt-7">
                  ✳ Dieses Dokument wurde von der Dickies-Campus GmbH ausgestellt und digital bestätigt.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-10 text-center" data-testid="verify-invalid">
              <p className="font-display italic text-[22px]" style={{ color: TERRA }}>
                Keine gültige Zertifikatsnummer.
              </p>
              <p className="text-sm text-sand-600 mt-3">
                Bitte prüfe die Nummer — sie steht unter dem Barcode auf dem Zertifikat (z. B. DH-2026-0001).
              </p>
            </div>
          )
        )}

        {/* Fußzeile */}
        <div className="mt-16 text-center">
          <div className="mx-auto mb-4" style={{ width: 60, height: 1, backgroundColor: GOLD }} />
          <p className="text-[10px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.28em' }}>
            Dickies-Campus.de · Wissen, das weiterhilft.
          </p>
          <a href="/barrierefreiheit" className="inline-block mt-3 text-[12px] font-semibold hover:underline underline-offset-4" style={{ color: TERRA }} data-testid="verify-accessibility-link">
            Erklärung zur Barrierefreiheit
          </a>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificatePage;
